import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { X, CheckCircle2 } from "lucide-react";
import { EASE } from "./Reveal";
import { useContent } from "../context/ContentContext";
import { CONTACT_DETAILS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WhatsAppGlyph = ({ size = 26 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const inputCls =
  "w-full bg-white border border-forest/20 px-4 py-3.5 text-sm text-ink placeholder:text-sage/60 transition-colors duration-300 focus:border-gold focus:outline-none";

const Field = ({ label, htmlFor, ...props }) => (
  <div>
    <label htmlFor={htmlFor} className="block text-[11px] tracking-[0.2em] uppercase font-bold text-forest mb-2">
      {label}
    </label>
    <input id={htmlFor} required className={inputCls} {...props} />
  </div>
);

const INITIAL = { first_name: "", last_name: "", mobile: "", email: "" };

export const WhatsAppButton = () => {
  const content = useContent();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(INITIAL);

  const phone = content?.contact?.phone || CONTACT_DETAILS.phone;
  const waNumber = phone.replace(/\(0\)/g, "").replace(/[^\d]/g, "");

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const close = () => {
    setOpen(false);
    setTimeout(() => {
      setDone(false);
      setForm(INITIAL);
    }, 350);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/whatsapp-leads`, form);
    } catch (err) {
      // WhatsApp handoff continues even if the notification fails
    }
    const message = `Hi, I'm ${form.first_name} ${form.last_name}, my mobile is ${form.mobile} and email is ${form.email}. I'd like to get in touch.`;
    window.open(`https://wa.me/${waNumber}?text=${encodeURIComponent(message)}`, "_blank", "noopener,noreferrer");
    setBusy(false);
    setDone(true);
  };

  return (
    <>
      <motion.button
        data-testid="whatsapp-button"
        onClick={() => setOpen(true)}
        aria-label="Chat with us on WhatsApp"
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 1.2, ease: EASE }}
        className="fixed bottom-5 right-5 sm:bottom-7 sm:right-7 z-[60] w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-[0_12px_30px_rgba(37,211,102,0.4)] transition-transform duration-300 hover:scale-110 active:scale-95"
      >
        <span aria-hidden="true" className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-20" />
        <WhatsAppGlyph />
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="whatsapp-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[70] bg-forest-deep/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
            onClick={close}
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.97 }}
              transition={{ duration: 0.45, ease: EASE }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full sm:max-w-md bg-cream p-7 sm:p-10 shadow-[0_30px_80px_rgba(0,0,0,0.45)] max-h-[92vh] overflow-y-auto"
            >
              <button
                data-testid="whatsapp-modal-close"
                onClick={close}
                aria-label="Close"
                className="absolute top-4 right-4 p-2.5 text-forest/60 hover:text-forest transition-colors"
              >
                <X size={20} />
              </button>

              {done ? (
                <div data-testid="whatsapp-success" className="text-center py-6">
                  <CheckCircle2 size={44} strokeWidth={1.2} className="text-gold-dark mx-auto" />
                  <h3 className="font-serif text-3xl font-medium text-forest mt-5">You&rsquo;re On Your Way</h3>
                  <p className="text-[13.5px] leading-[1.75] text-sage mt-4 max-w-xs mx-auto">
                    WhatsApp should now be open with your message ready to send. We look forward to
                    speaking with you.
                  </p>
                  <button onClick={close} data-testid="whatsapp-done-close" className="btn-forest mt-8">
                    Close
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-1">
                    <span className="w-10 h-10 rounded-full bg-[#25D366] text-white flex items-center justify-center">
                      <WhatsAppGlyph size={20} />
                    </span>
                    <p className="eyebrow text-gold-dark">WhatsApp Us</p>
                  </div>
                  <h3 className="font-serif text-3xl font-medium text-forest mt-3">Let&rsquo;s Talk</h3>
                  <div className="w-12 h-px bg-gold my-5" />
                  <p className="text-[13px] leading-[1.7] text-sage mb-7">
                    Leave your details and continue straight into WhatsApp — your message will be
                    pre-filled and ready to send.
                  </p>
                  <form onSubmit={submit} className="space-y-5" data-testid="whatsapp-form">
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="First Name" htmlFor="wa-first-name" data-testid="wa-field-first-name" value={form.first_name} onChange={set("first_name")} placeholder="First name" />
                      <Field label="Last Name" htmlFor="wa-last-name" data-testid="wa-field-last-name" value={form.last_name} onChange={set("last_name")} placeholder="Last name" />
                    </div>
                    <Field label="Mobile Number" htmlFor="wa-mobile" data-testid="wa-field-mobile" type="tel" value={form.mobile} onChange={set("mobile")} placeholder="+44 7700 900000" />
                    <Field label="Email Address" htmlFor="wa-email" data-testid="wa-field-email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" />
                    <button type="submit" data-testid="wa-continue-button" disabled={busy} className="btn-gold w-full justify-center disabled:opacity-60">
                      {busy ? "Please wait..." : "Continue to WhatsApp"} <WhatsAppGlyph size={16} />
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
